import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CreditCard, Check, X, Clock, AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

export const ExtraCostRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveAmountEdit, setApproveAmountEdit] = useState({});

  const fetchRequests = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`/extra-cost/requests${params}`);
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Failed to load extra cost requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [filter]);

  const handleApprove = async (req) => {
    try {
      const amount = approveAmountEdit[req.id] ?? req.amount;
      await axios.put(`/extra-cost/requests/${req.id}/approve`, { approved_amount: parseFloat(amount) });
      toast.success(`Approved Rs.${amount} for ${req.employee_name}`);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectId) return;
    try {
      await axios.put(`/extra-cost/requests/${rejectId}/reject`, { reason: rejectReason });
      toast.success('Request rejected');
      setShowRejectDialog(false);
      setRejectId(null);
      setRejectReason('');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" /> },
      approved: { color: 'bg-green-100 text-green-700', icon: <Check className="w-3 h-3" /> },
      rejected: { color: 'bg-red-100 text-red-700', icon: <X className="w-3 h-3" /> },
    };
    const s = map[status] || map.pending;
    return <Badge className={`${s.color} flex items-center gap-1 text-xs`}>{s.icon}{status?.charAt(0).toUpperCase() + status?.slice(1)}</Badge>;
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="extra-cost-requests-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-600" />
            Extra Cost Requests
            {pendingCount > 0 && <Badge className="bg-amber-500 text-white ml-2">{pendingCount} pending</Badge>}
          </h2>
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} className={filter === f ? 'bg-amber-600 hover:bg-amber-700' : ''}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-amber-500"></div></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No extra cost requests found</p></div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-800">
                <TableHead className="text-white font-semibold text-xs">Case ID</TableHead>
                <TableHead className="text-white font-semibold text-xs">Check ID</TableHead>
                <TableHead className="text-white font-semibold text-xs">Employee</TableHead>
                <TableHead className="text-white font-semibold text-xs">Raised By</TableHead>
                <TableHead className="text-white font-semibold text-xs">Vendor</TableHead>
                <TableHead className="text-white font-semibold text-xs">Remarks</TableHead>
                <TableHead className="text-white font-semibold text-xs text-center">Cost Requested</TableHead>
                <TableHead className="text-white font-semibold text-xs text-center">Status</TableHead>
                <TableHead className="text-white font-semibold text-xs text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id} className="hover:bg-gray-50">
                  <TableCell className="text-xs font-mono">{req.case_id}</TableCell>
                  <TableCell className="text-xs font-mono">{req.check_id}</TableCell>
                  <TableCell className="text-xs font-medium">{req.employee_name}</TableCell>
                  <TableCell className="text-xs">{req.raised_by}</TableCell>
                  <TableCell className="text-xs">{req.vendor_name}</TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate" title={req.remarks}>{req.remarks}</TableCell>
                  <TableCell className="text-center">
                    {req.status === 'pending' ? (
                      <Input type="number" className="w-24 h-7 text-xs text-center mx-auto" value={approveAmountEdit[req.id] ?? req.amount}
                        onChange={(e) => setApproveAmountEdit(prev => ({...prev, [req.id]: e.target.value}))} />
                    ) : (
                      <span className="text-xs font-bold">Rs.{req.approved_amount || req.amount}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">{statusBadge(req.status)}</TableCell>
                  <TableCell className="text-center">
                    {req.status === 'pending' ? (
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs px-2" onClick={() => handleApprove(req)} data-testid={`approve-ecr-${req.id}`}>
                          <Check className="w-3 h-3 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-300 hover:bg-red-50 text-xs px-2" onClick={() => { setRejectId(req.id); setShowRejectDialog(true); }} data-testid={`reject-ecr-${req.id}`}>
                          <X className="w-3 h-3 mr-1" />Reject
                        </Button>
                      </div>
                    ) : req.status === 'approved' ? (
                      <span className="text-xs text-green-600">by {req.approved_by}</span>
                    ) : (
                      <span className="text-xs text-red-600">{req.rejection_reason || 'Rejected'}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Reject Extra Cost</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Reason for rejection</Label><Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter reason..." rows={3} /></div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleReject}>Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
